/**
 * Icon name -> Lucide component lookup.
 *
 * Categories and achievements store icons as strings so the persisted JSON
 * stays serializable. The UI resolves through this map. If a new category
 * uses an icon not listed here, it falls back to a generic shape.
 */

import {
  Activity,
  Award,
  Bed,
  BookOpen,
  Briefcase,
  Bus,
  CalendarCheck,
  CalendarDays,
  Car,
  Coffee,
  CreditCard,
  Dog,
  DollarSign,
  Dumbbell,
  Film,
  Flame,
  Footprints,
  Gamepad2,
  Gift,
  GraduationCap,
  Hand,
  Heart,
  Home,
  Inbox,
  Laptop,
  MapPin,
  MoreHorizontal,
  Music,
  Phone,
  PieChart,
  PiggyBank,
  Pill,
  Plane,
  Rocket,
  Scissors,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Stethoscope,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  UtensilsCrossed,
  Wifi,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

export const ICONS: Record<string, LucideIcon> = {
  Activity,
  Award,
  Bed,
  BookOpen,
  Briefcase,
  Bus,
  CalendarCheck,
  CalendarDays,
  Car,
  Coffee,
  CreditCard,
  Dog,
  DollarSign,
  Dumbbell,
  Film,
  Flame,
  Footprints,
  Gamepad2,
  Gift,
  GraduationCap,
  Hand,
  Heart,
  Home,
  Inbox,
  Laptop,
  MapPin,
  MoreHorizontal,
  Music,
  Phone,
  PieChart,
  PiggyBank,
  Pill,
  Plane,
  Rocket,
  Scissors,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Stethoscope,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  UtensilsCrossed,
  Wifi,
  Wrench,
  Zap,
};

/**
 * Curated icon set offered in the category picker. Ordered roughly by
 * usefulness — common spend / income concepts first.
 */
export const CATEGORY_ICON_NAMES: string[] = [
  'UtensilsCrossed',
  'Coffee',
  'Car',
  'Bus',
  'Plane',
  'Home',
  'Bed',
  'Zap',
  'Wifi',
  'Phone',
  'Film',
  'Music',
  'Gamepad2',
  'ShoppingBag',
  'ShoppingCart',
  'Gift',
  'Heart',
  'Pill',
  'Stethoscope',
  'Dumbbell',
  'GraduationCap',
  'BookOpen',
  'Sparkles',
  'Scissors',
  'Dog',
  'CreditCard',
  'Briefcase',
  'Laptop',
  'Rocket',
  'TrendingUp',
  'PiggyBank',
  'DollarSign',
  'Star',
  'MapPin',
  'Shield',
  'MoreHorizontal',
];

/**
 * Curated color palette offered in the category picker. iOS system colors,
 * 12 hues that read distinctly in both light and dark mode.
 */
export const CATEGORY_COLORS: string[] = [
  '#FF3B30', // red
  '#FF9500', // orange
  '#FFCC00', // yellow
  '#34C759', // green
  '#00C7BE', // mint
  '#5AC8FA', // teal
  '#007AFF', // blue
  '#5856D6', // indigo
  '#AF52DE', // purple
  '#FF2D55', // pink
  '#A2845E', // brown
  '#8E8E93', // gray
];

/** Resolve a stored icon name; falls back to a neutral shape. */
export function resolveIcon(name: string): LucideIcon {
  return ICONS[name] ?? MoreHorizontal;
}
